import React, { memo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import classNames from 'classnames';

import InformationCard, { IInformationCardProps } from '../../../../components/InformationCard/InformationCard';
import { SLOT_TIME_FORMAT_SMALL } from '../../../../constants/dates.constants';
import meetingImage from '../../../../assets/images/meeting.svg';
import Avatar from '../../../../components/Avatar/Avatar';
import { ISlot } from '../../store/meeting/meeting.types';
import { TFullSender } from '../../../../store/users/users.types';

import useStyles from './meetingInfoCard.styles';

export interface IMeetingInfoCardProps extends Omit<IInformationCardProps, 'slot'> {
  sender: TFullSender;
  showSenderAvatar?: boolean;
  slot: ISlot;
  timezoneName: string;
  notes?: string;
}

const MeetingInfoCard = ({
  sender,
  showSenderAvatar = false,
  slot,
  timezoneName,
  notes,
  ...infoCardProps
}: IMeetingInfoCardProps) => {
  const classes = useStyles();
  const { palette: colors } = useTheme();

  const start = moment.utc(slot.startAt).tz(timezoneName).format(SLOT_TIME_FORMAT_SMALL);
  const end = moment.utc(slot.endAt).tz(timezoneName).format(SLOT_TIME_FORMAT_SMALL);
  const dateFormat = 'dddd, MMMM DD, YYYY';

  const meetingType = sender.meetingType?.toLowerCase();
  const meetingTypePhrase = meetingType === 'in person' ? `${meetingType} meeting` : meetingType;

  return (
    <InformationCard borderColor={colors.green.main} {...infoCardProps}>
      <Box width={1} display="flex" justifyContent="flex-start" alignItems="flex-start">
        <Box className={classes.avatarWrapper}>
          {sender.image && showSenderAvatar ? (
            <Avatar className={classes.avatar} src={sender.image} alt="Sender avatar" />
          ) : (
            <img className={classes.image} src={meetingImage} alt="Meeting" />
          )}
        </Box>
        <Box textAlign="left">
          <Box mb={[1, 2]}>
            <Typography
              data-testid="meetingInfoCardTitle"
              className={classNames('Body-Small-Success', classes.subTitle)}
            >
              {sender.meetingType ? (
                <>
                  {sender.firstName} will reach out to you for your <b>{meetingTypePhrase}</b>
                </>
              ) : (
                <>
                  Your meeting with <b>{sender.firstName}</b>
                </>
              )}
            </Typography>
          </Box>
          <Box mb={1 / 2}>
            <Typography data-testid="meetingInfoCardDate" className="H4-Dark-Bold">
              {moment(slot.startAt).format(dateFormat)}
            </Typography>
          </Box>
          <Typography data-testid="meetingInfoCardTime" className="Body-Regular-Left-Static">
            {start} - {end}
          </Typography>
          {notes && (
            <Box mt={2}>
              <Typography className="Body-Regular-Left-Static-Bold">Your meeting notes:</Typography>
              <Typography className={classNames('Body-Regular-Left-Static', classes.notes)}>{notes}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </InformationCard>
  );
};

export default memo(MeetingInfoCard);
